// ===============================================
// Create Goods Receipt Page (New Component)
// หน้าสร้างใบรับสินค้าเข้าคลัง - การจัดการสต็อกอัตโนมัติ
// ===============================================

import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Table,
  InputNumber,
  Input,
  Space,
  Typography,
  Divider,
  message,
  Tag,
  Alert,
  Tooltip,
} from 'antd';
import {
  SaveOutlined,
  CheckOutlined,
  ArrowLeftOutlined,
  InboxOutlined,
  WarningOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  purchasesAPI,
  goodsReceiptsAPI,
  formatCurrency,
  formatDate,
  handleAPIError,
  GR_STATUS,
  PO_STATUS
} from '../../services/PurchaseService';
import { useAuth } from '../../contexts/AuthContext'

const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;

const CreateReceiptPage = () => {
  const { user, hasPermission } = useAuth()
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [pendingOrders, setPendingOrders] = useState([]);
  const [receiptItems, setReceiptItems] = useState([]);
  const [totals, setTotals] = useState({
    ordered: 0,
    received: 0,
    amount: 0,
  });

  // ดึงข้อมูลใบสั่งซื้อที่รอรับสินค้า
  const fetchPendingOrders = async () => {
    try {
      const response = await goodsReceiptsAPI.getPendingOrders();
      console.log('pending Ordr:', response.data);
      if (response.success) {
        setPendingOrders(response.data);
      }
    } catch (error) {
      message.error(handleAPIError(error));
    }
  };

  // ดึงข้อมูลใบสั่งซื้อที่เลือก
  const fetchPurchaseOrder = async (selectedPoId) => {
    console.log('selected poId:', selectedPoId);
    if (!selectedPoId) return;

    setLoading(true);
    try {
      const response = await goodsReceiptsAPI.getPendingOrderById(selectedPoId);
      if (response.success) {
        const po = response.data;

        //console.log('PO data', po);

        setPurchaseOrder(po);

        // เตรียมรายการสินค้าสำหรับรับสินค้า
        const items = po.items.map(item => ({
          ...item,
          received_quantity: 0, // จำนวนที่รับจริง
          received_total: 0,
          remaining_quantity_ordered: item.quantity_ordered, // จำนวนที่ยังคงเหลือ
        }));
        setReceiptItems(items);

        // ตั้งค่าเริ่มต้นในฟอร์ม
        form.setFieldsValue({
          id: selectedPoId,
          receipt_date: dayjs(),
          supplier_name: po.supplier_name,
          purchase_number: po.purchase_number,
        });
      }
    } catch (error) {
      message.error(handleAPIError(error));
    } finally {
      setLoading(false);
    }
  };

  // เลือกใบสั่งซื้อ
  const handlePOSelect = (selectedPoId) => {
    fetchPurchaseOrder(selectedPoId);
  };

  // อัพเดตจำนวนที่รับ
  // const handleReceivequantity_orderedChange = (index, value) => {
  //   const newItems = [...receiptItems];
  //   const item = newItems[index];

  //   // ตรวจสอบไม่ให้เกินจำนวนที่สั่ง
  //   if (value > item.quantity_ordered) {
  //     message.warning(`จำนวนที่รับไม่สามารถเกินจำนวนที่สั่ง (${item.quantity_ordered})`);
  //     return;
  //   }
    
  //   console.log('items', item);

  //   item.received_quantity_ordered = value || 0;
  //   item.received_total = parseFloat(item.received_quantity_ordered) * parseFloat(item.unit_price);
  //   item.remaining_quantity_ordered = parseFloat(item.quantity_ordered) - parseFloat(item.received_quantity_ordered);

  //   setReceiptItems(newItems);
  //   calculateTotals(newItems);
  // };

    const handleReceivequantity_orderedChange = (index, value) => {
    const newItems = [...receiptItems];
    const item = newItems[index];

    // แปลง value เป็น number
    const receivedQty = Number(value) || 0;
    const orderedQty = Number(item.quantity_ordered) || 0;

    // ตรวจสอบไม่ให้เกินจำนวนที่สั่ง
    if (receivedQty > orderedQty) {
      message.warning(`จำนวนที่รับไม่สามารถเกินจำนวนที่สั่ง (${orderedQty})`);
      return;
    }

    item.received_quantity = receivedQty;
    item.received_total = receivedQty * (Number(item.unit_cost) || 0);
    item.remaining_quantity_ordered = orderedQty - receivedQty;

    setReceiptItems(newItems);
    calculateTotals(newItems);
  };


  // คำนวณยอดรวม
  const calculateTotals = (items = receiptItems) => {
    console.log('receiptItems', receiptItems);
    const ordered = items.reduce((sum, item) => sum + item.quantity_ordered, 0);
    const received = items.reduce((sum, item) => sum + item.received_quantity, 0);
    const amount = items.reduce((sum, item) => sum + item.received_total, 0);

    setTotals({ ordered, received, amount });
  };

  // รับสินค้าทั้งหมด (จำนวนเท่ากับที่สั่ง)
  const handleReceiveAll = () => {
    const newItems = receiptItems.map(item => ({
      ...item,
      received_quantity: item.quantity_ordered,
      received_total: item.quantity_ordered * item.unit_price,
      remaining_quantity_ordered: 0,
    }));
    setReceiptItems(newItems);
    calculateTotals(newItems);
  };

  // ล้างการรับสินค้า
  const handleClearAll = () => {
    const newItems = receiptItems.map(item => ({
      ...item,
      received_quantity: 0,
      received_total: 0,
      remaining_quantity_ordered: item.quantity_ordered,
    }));
    setReceiptItems(newItems);
    calculateTotals(newItems);
  };

  // บันทึกใบรับสินค้า
  const handleSave = async (status = GR_STATUS.DRAFT) => {
    try {

      const received_by = user.id;
      const values = await form.validateFields();
      
      console.log('value', values);

      // ตรวจสอบว่ามีการรับสินค้าหรือไม่
      const hasReceivedItems = receiptItems.some(item => item.received_quantity > 0);
      if (!hasReceivedItems) {
        message.warning('กรุณาระบุจำนวนที่รับสินค้าอย่างน้อย 1 รายการ');
        return;
      }

      console.log('submit Items', receiptItems);

      setSaveLoading(true);

      const receiptData = {
        ...values,
        status,
        purchase_id: id,
        items: receiptItems.filter(item => item.received_quantity > 0),
        total_received: totals.received,
        total_amount: totals.amount,
        receipt_date: values.receipt_date.format('YYYY-MM-DD'),
        notes: values.notes,
        received_by
      };

      console.log('Sending receiptData:', receiptData);

      const response = await goodsReceiptsAPI.create(receiptData);

      if (response.success) {
        message.success(`${status === GR_STATUS.CONFIRMED ? 'ยืนยัน' : 'บันทึก'}การรับสินค้าสำเร็จ`);
        navigate('/purchases/goods-receipts');
      }
    } catch (error) {
      message.error(handleAPIError(error));
    } finally {
      setSaveLoading(false);
    }
  };

  // คอลัมน์ของตารางรายการสินค้า
  const itemColumns = [
    {
      title: 'รหัสสินค้า',
      dataIndex: 'barcode',
      key: 'barcode',
      width: 120,
    },
    {
      title: 'ชื่อสินค้า',
      dataIndex: 'product_name',
      key: 'product_name',
      ellipsis: true,
    },
    {
      title: 'ราคาต่อหน่วย',
      dataIndex: 'unit_cost',
      key: 'unit_cost',
      width: 120,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
    {
      title: 'สั่งซื้อ',
      dataIndex: 'quantity_ordered',
      key: 'quantity_ordered',
      width: 80,
      align: 'center',
    },
    {
      title: 'รับจริง',
      dataIndex: 'received_quantity',
      key: 'received_quantity',
      width: 120,
      render: (value, record, index) => (
        <InputNumber
          style={{ width: '100%' }}
          value={value}
          min={0}
          max={record.quantity_ordered}
          onChange={(value) => handleReceivequantity_orderedChange(index, value || 0)}
        />
      ),
    },
    {
      title: 'คงเหลือ',
      dataIndex: 'remaining_quantity_ordered',
      key: 'remaining_quantity_ordered',
      width: 80,
      align: 'center',
      render: (value) => (
        <Tag color={value > 0 ? 'orange' : 'green'}>
          {value}
        </Tag>
      ),
    },
    {
      title: 'ยอดรับ',
      dataIndex: 'total_cost',
      key: 'total_cost',
      width: 130,
      align: 'right',
      render: (value) => formatCurrency(value),
    },
  ];

  // เรียกใช้เมื่อ component mount
  useEffect(() => {
    fetchPendingOrders();
    console.log('poId:', id);
    if (id) {
      fetchPurchaseOrder(id);
    }
  }, [id]);

  // คำนวณยอดรวมเมื่อรายการเปลี่ยน
  useEffect(() => {
    calculateTotals();
  }, [receiptItems]);

  return (
    <div className="create-goods-receipt">
      {/* Header */}
      <Card style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/purchases')}
              >
                กลับ
              </Button>
              <Title level={4} style={{ margin: 0 }}>
                <InboxOutlined /> สร้างใบรับสินค้าเข้าคลัง
              </Title>
            </Space>
          </Col>
          <Col>
            <Space>
              <Button
                loading={saveLoading}
                onClick={() => handleSave(GR_STATUS.DRAFT)}
              >
                บันทึกร่าง
              </Button>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                loading={saveLoading}
                onClick={() => handleSave(GR_STATUS.CONFIRMED)}
              >
                ยืนยันการรับสินค้า
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          receipt_date: dayjs(),
        }}
      >
        {/* ข้อมูลทั่วไป */}
        <Card title="ข้อมูลใบรับสินค้า" style={{ marginBottom: 24 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                label="ใบสั่งซื้อ"
                name="purchase_number"
                rules={[{ required: true, message: 'กรุณาเลือกใบสั่งซื้อ' }]}
              >
                <Select
                  placeholder="เลือกใบสั่งซื้อ"
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handlePOSelect}
                  loading={loading}
                >
                  {pendingOrders.map(order => (
                    <Option key={order.id} value={order.id}>
                      {order.purchase_number} - {order.supplier_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                label="วันที่รับสินค้า"
                name="receipt_date"
                rules={[{ required: true, message: 'กรุณาเลือกวันที่รับสินค้า' }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label="ผู้จำหน่าย" name="supplier_name">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label="เลขที่ใบสั่งซื้อ" name="purchase_number">
                <Input disabled />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item label="หมายเหตุ" name="notes">
                <TextArea rows={2} placeholder="หมายเหตุเพิ่มเติม" />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        {/* คำแนะนำการจัดการสต็อก */}
        {purchaseOrder && (
          <Alert
            style={{ marginBottom: 24 }}
            message="การจัดการสต็อกอัตโนมัติ"
            description="เมื่อยืนยันการรับสินค้า ระบบจะทำการเพิ่มสต็อกของสินค้าที่รับเข้ามาอัตโนมัติ"
            type="info"
            icon={<InfoCircleOutlined />}
            showIcon
          />
        )}

        {/* รายการสินค้า */}
        {receiptItems.length > 0 && (
          <Card
            title="รายการสินค้า"
            extra={
              <Space>
                <Button size="small" onClick={handleClearAll}>
                  ล้างทั้งหมด
                </Button>
                <Button type="primary" size="small" onClick={handleReceiveAll}>
                  รับทั้งหมด
                </Button>
              </Space>
            }
            style={{ marginBottom: 24 }}
          >
            <Table
              columns={itemColumns}
              dataSource={receiptItems}
              rowKey={(record, index) => record.id || index}
              pagination={false}
              size="small"
            />
          </Card>
        )}

        {/* สรุปยอด */}
        {receiptItems.length > 0 && (
          <Card title="สรุปการรับสินค้า">
            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <div style={{ fontSize: '16px' }}>
                  <div style={{ marginBottom: 8 }}>
                    <Text>จำนวนที่สั่งซื้อ: </Text>
                    <Text strong>{totals.ordered} หน่วย</Text>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Text>จำนวนที่รับ: </Text>
                    <Text strong style={{ color: '#52c41a' }}>
                      {totals.received} หน่วย
                    </Text>
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <Text>คงเหลือ: </Text>
                    <Text strong style={{ color: totals.ordered - totals.received > 0 ? '#fa8c16' : '#52c41a' }}>
                      {totals.ordered - totals.received} หน่วย
                    </Text>
                  </div>
                </div>
              </Col>
              <Col xs={24} lg={12}>
                <div style={{ textAlign: 'right', fontSize: '18px' }}>
                  <div style={{ marginBottom: 16 }}>
                    <Text>มูลค่ารวมของสินค้าที่รับ: </Text>
                  </div>
                  <div>
                    <Text strong style={{ color: '#1890ff', fontSize: '20px' }}>
                      {formatCurrency(totals.amount)}
                    </Text>
                  </div>
                </div>
              </Col>
            </Row>
          </Card>
        )}
      </Form>
    </div>
  );
};

export default CreateReceiptPage;